import { sampleField } from '../data/forecast.js';
import { rainColor, tempColor } from '../data/palette.js';

/**
 * Canvas overlay for soft forecast fields.
 * Continuous RAF only while wind particles need it.
 */
export function createForecastOverlay(map, canvas) {
  const ctx = canvas.getContext('2d', { alpha: true });
  let particles = seedParticles(90);
  let raf = 0;
  let last = performance.now();
  let animating = false;
  let stateRef = { current: null };

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const { clientWidth: w, clientHeight: h } = canvas.parentElement;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  resize();
  window.addEventListener('resize', resize);
  map.on('resize', resize);

  function paint(state) {
    const { clientWidth: w, clientHeight: h } = canvas.parentElement;
    ctx.clearRect(0, 0, w, h);
    const frame = state.timeline[state.index];
    if (!frame || frame.kind !== 'forecast' || !state.forecast) return;
    const fi = frame.forecastIndex ?? 0;

    if (state.layer === 'wind') {
      paintWindWash(ctx, map, state.forecast, fi, w, h);
      const now = performance.now();
      stepParticles(particles, map, state.forecast, fi, w, h, Math.min(0.05, (now - last) / 1000));
      drawParticles(ctx, particles, map, state.forecast, fi, w, h);
      last = now;
    } else {
      paintScalarField(ctx, map, state, fi, w, h);
    }
  }

  function loop() {
    if (!animating || !stateRef.current) return;
    paint(stateRef.current);
    raf = requestAnimationFrame(loop);
  }

  return {
    resize,
    paint,
    setAnimating(on, ref) {
      stateRef = ref || stateRef;
      if (on === animating) return;
      animating = on;
      cancelAnimationFrame(raf);
      if (on) {
        last = performance.now();
        raf = requestAnimationFrame(loop);
      }
    },
    start(ref) {
      stateRef = ref;
      paint(ref.current);
    },
    stop() {
      animating = false;
      cancelAnimationFrame(raf);
    },
  };
}

function paintScalarField(ctx, map, state, fi, w, h) {
  const step = 12;
  const field = state.layer === 'temp' ? 'temperature_2m' : 'precipitation';
  const colorFn = state.layer === 'temp' ? tempColor : rainColor;
  const bw = Math.ceil(w / step);
  const bh = Math.ceil(h / step);
  const img = ctx.createImageData(bw, bh);
  for (let y = 0; y < bh; y++) {
    for (let x = 0; x < bw; x++) {
      const lngLat = map.unproject([x * step + step / 2, y * step + step / 2]);
      const v = sampleField(state.forecast, field, fi, lngLat.lng, lngLat.lat);
      const show = state.layer === 'temp' ? true : v > 0.05;
      const c = show ? colorFn(v) : [0, 0, 0, 0];
      const i = (y * bw + x) * 4;
      img.data[i] = Math.round(c[0] * 255);
      img.data[i + 1] = Math.round(c[1] * 255);
      img.data[i + 2] = Math.round(c[2] * 255);
      img.data[i + 3] = Math.round(c[3] * 255);
    }
  }
  const tmp = document.createElement('canvas');
  tmp.width = bw;
  tmp.height = bh;
  tmp.getContext('2d').putImageData(img, 0, 0);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.globalAlpha = 0.85;
  ctx.filter = 'blur(10px)';
  ctx.drawImage(tmp, 0, 0, w, h);
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
}

function paintWindWash(ctx, map, grid, fi, w, h) {
  const step = 16;
  const bw = Math.ceil(w / step);
  const bh = Math.ceil(h / step);
  const img = ctx.createImageData(bw, bh);
  for (let y = 0; y < bh; y++) {
    for (let x = 0; x < bw; x++) {
      const ll = map.unproject([x * step + step / 2, y * step + step / 2]);
      const sp = sampleField(grid, 'wind_speed_10m', fi, ll.lng, ll.lat);
      const a = Math.min(0.35, sp / 35);
      const i = (y * bw + x) * 4;
      img.data[i] = 140;
      img.data[i + 1] = 190;
      img.data[i + 2] = 255;
      img.data[i + 3] = Math.round(a * 255);
    }
  }
  const tmp = document.createElement('canvas');
  tmp.width = bw;
  tmp.height = bh;
  tmp.getContext('2d').putImageData(img, 0, 0);
  ctx.filter = 'blur(12px)';
  ctx.globalAlpha = 0.9;
  ctx.drawImage(tmp, 0, 0, w, h);
  ctx.filter = 'none';
  ctx.globalAlpha = 1;
}

function seedParticles(n) {
  return Array.from({ length: n }, () => ({
    x: Math.random(),
    y: Math.random(),
    life: Math.random(),
    len: 6 + Math.random() * 10,
  }));
}

function stepParticles(particles, map, grid, fi, w, h, dt) {
  particles.forEach((p) => {
    const x = p.x * w;
    const y = p.y * h;
    const ll = map.unproject([x, y]);
    const spd = sampleField(grid, 'wind_speed_10m', fi, ll.lng, ll.lat);
    const dir = sampleField(grid, 'wind_direction_10m', fi, ll.lng, ll.lat);
    const rad = ((dir + 180) * Math.PI) / 180;
    const v = Math.max(10, spd * 2.2);
    p.x += (Math.sin(rad) * v * dt) / w;
    p.y += (-Math.cos(rad) * v * dt) / h;
    p.life -= dt * 0.18;
    p._rad = rad;
    p._spd = spd;
    if (p.life <= 0 || p.x < -0.05 || p.x > 1.05 || p.y < -0.05 || p.y > 1.05) {
      p.x = Math.random();
      p.y = Math.random();
      p.life = 1;
      p.len = 6 + Math.random() * 10;
    }
  });
}

function drawParticles(ctx, particles, map, grid, fi, w, h) {
  ctx.lineCap = 'round';
  particles.forEach((p) => {
    const x = p.x * w;
    const y = p.y * h;
    const rad = p._rad ?? 0;
    const len = p.len * (0.6 + Math.min(1.4, (p._spd || 5) / 25));
    ctx.strokeStyle = `rgba(230, 242, 255, ${Math.max(0.2, p.life * 0.75)})`;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(x - Math.sin(rad) * len * 0.35, y + Math.cos(rad) * len * 0.35);
    ctx.lineTo(x + Math.sin(rad) * len * 0.65, y - Math.cos(rad) * len * 0.65);
    ctx.stroke();
  });
}
